import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, Observable, Subscription, take } from 'rxjs';

import { DialogLoadingComponent } from 'src/app/shared/components/dialog-loading/dialog-loading.component';
import { DEVICE_TYPES, DeviceType } from 'src/app/shared/interfaces/ISettings';
import { CreateDispense } from 'src/app/shared/models/dispense.models';
import { ApiErrorHandlerService } from 'src/app/shared/services/api-error-handler/api-error-handler.service';
import { TSettings } from 'src/app/shared/services/appconfig/settings.model';
import { HelmsdeepService } from 'src/app/shared/services/helmsdeep/helmsdeep.service';
import { LoadFlowOriginService } from 'src/app/shared/services/load-flow-origin/load-flow-origin.service';
import { OrderProcessingService } from 'src/app/shared/services/order-processing/order-processing.service';
import { DISPENSE_STATES, PRESCRIPTIVE_FLOW } from 'src/app/shared/utils/constants';
import { Utils } from 'src/app/shared/utils/utils';
import { selectSettings } from 'src/app/stores/settings';
import { loadSpaces } from 'src/app/stores/spaces';
import { isSizeSelectionAvailable } from 'src/app/stores/spaces/spaces.selector';

import { GuidedLoadingContext, GuidedLoadingPaths } from '../../interfaces/IGuidedLoadingContext';
import { Order } from '../../interfaces/IOrder';
import { IActions } from '../../interfaces/IScheduleActionParams';
import { LOADING_STORE, LoadingStore } from '../../services/store/loading-store.token';
import { getOnCancelRedirectPath } from '../../utils/guided-loading.util';

@Component({
  selector: 'frodo-loading-order',
  templateUrl: './loading-order.component.html',
  styleUrls: ['./loading-order.component.scss'],
})
export class LoadingOrderComponent implements OnInit, OnDestroy {
  title = '';
  backgroundColorCancel = '#F53737';
  backgroundColorAddSpace = '#007698';
  backgroundColorComplete = '#25A011';
  logoAlt = 'logo';
  logo = './../../assets/logo.png';
  titleLoadingOrder = '';
  titleLoadOrder = '';
  buttonTextCancel = '';
  buttonTextAddSpace = '';
  buttonTextComplete = '';
  noSpaceAvailable = '';

  cancelOrderInterval: any;
  isLoading = false;
  flow = '';
  type = '';
  showButtons = false;
  inactivityInterval: any;
  latestTap: number;
  timer: number;
  guidedLoadFailForward = false;
  dialogInstance: any;
  expireLoaded = false;
  subscription: Subscription = new Subscription();
  hdSettings$: Observable<Partial<TSettings>>;
  disableAddCompartmentButton = false;
  scheduleId = '';
  allowAlphanumeric = false;
  digitLength: number;
  deviceType: DeviceType;
  frodoContext = '';
  paths: GuidedLoadingPaths;

  @Input() actionsEnabled = true;
  @Output() inactivityTimeoutElapsed = new EventEmitter<void>();

  private routeData: GuidedLoadingContext;

  constructor(
    private activatedRoute: ActivatedRoute,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private helmsdeepService: HelmsdeepService,
    public router: Router,
    public dialog: MatDialog,
    private store: Store,
    private utils: Utils,
    @Inject(LOADING_STORE) private readonly loadingStore: LoadingStore,
    private orderProcessingService: OrderProcessingService,
    private loadFlowOrigin: LoadFlowOriginService
  ) {}

  ngOnInit(): void {
    this.routeData = this.activatedRoute.snapshot.data as GuidedLoadingContext;
    const { frodoContext, paths, translations } = this.routeData;
    this.frodoContext = frodoContext;
    this.paths = paths;
    this.titleLoadingOrder = translations.titleLoadingOrder;
    this.titleLoadOrder = translations.titleLoadOrder;
    this.buttonTextCancel = translations.buttonTextCancel;
    this.buttonTextAddSpace = translations.buttonTextAddSpace;
    this.buttonTextComplete = translations.buttonTextComplete;
    this.noSpaceAvailable = translations.noSpaceAvailable;

    this.store.dispatch(loadSpaces());
    this.loadingStore.decreaseSpacesAvailable();

    this.subscription.add(
      this.store.select(selectSettings).subscribe((settings) => {
        const frodoSettings = settings.apps.frodo;
        this.allowAlphanumeric = settings.accessCode.allowAlphanumeric;
        this.digitLength =
          settings.accessCode.generatedCodeLength || settings.accessCode.length.maximum;
        this.expireLoaded = settings.defaultSettings.expireLoaded;
        this.deviceType = settings.device.deviceType;
        this.flow = this.activatedRoute.snapshot.paramMap.get('flow');
        this.type = this.activatedRoute.snapshot.paramMap.get('type');
        this.scheduleId = this.activatedRoute.snapshot.paramMap.get('scheduleId');
        if (this.flow === PRESCRIPTIVE_FLOW) {
          this.showButtons = true;
          this.title = this.titleLoadOrder;
          this.timer = frodoSettings.timeouts.guidedOnloadOrder;
          this.startInactivityTimer();
        } else {
          this.showButtons = false;
          this.title = this.titleLoadingOrder;
          this.timer = frodoSettings.timeouts.guidedOffloadOrder;
          this.timeOutToCancelOrder();
        }
      })
    );
    this.subscription.add(
      this.loadingStore.store$.subscribe((state) => {
        const reachedMax = state.spacesAvailable < 1;

        this.disableAddCompartmentButton = reachedMax;
        this.backgroundColorAddSpace = reachedMax ? '#ccc' : '#007698';
      })
    );

    this.subscription.add(
      this.utils.getLoadingValue().subscribe((value) => {
        this.isLoading = value;
      })
    );
  }

  timeOutToCancelOrder(): void {
    this.cancelOrderInterval = setInterval(() => {
      this.router.navigate([
        `${this.frodoContext}/${this.paths.PROCESSING_ORDER}`,
        { type: this.type, flow: this.flow, scheduleId: this.scheduleId },
      ]);
    }, this.timer);
  }

  private startInactivityTimer(): void {
    clearTimeout(this.inactivityInterval);
    this.inactivityInterval = setTimeout(() => {
      if (this.inactivityTimeoutElapsed.observed) {
        this.inactivityTimeoutElapsed.emit();
      } else {
        this.onComplete();
      }
    }, this.timer);
  }

  getTargetScreen(): string {
    return getOnCancelRedirectPath(this.routeData.frodoContext, this.type);
  }

  onCancel(): void {
    clearInterval(this.cancelOrderInterval);
    clearTimeout(this.inactivityInterval);
    this.utils.cancelLoading({
      frodoContext: this.frodoContext,
      targetScreen: this.getTargetScreen(),
      type: this.type,
      flow: this.flow,
      scheduleId: this.scheduleId,
    });
  }

  onAddSpace(): void {
    if (this.disableAddCompartmentButton || !this.actionsEnabled) return;
    clearInterval(this.inactivityInterval);
    this.dialogInstance = this.dialog.open(DialogLoadingComponent, {
      disableClose: true,
      backdropClass: 'modal-blurred-background',
    });

    this.subscription.add(
      this.helmsdeepService
        .getCurrentDispense(this.scheduleId, DISPENSE_STATES.LOADING)
        .pipe(
          catchError((error) => {
            this.apiErrorHandlerService.handleApiError(error, '');
            this.errorActions();
            return EMPTY;
          })
        )
        .subscribe((verify: boolean) => {
          this.createDispense(verify);
        })
    );

    this.dialogInstance.afterClosed().subscribe((result) => {
      if (result && result.data === 'goBack') {
        this.onCancel();
      }
    });
  }

  onComplete(): void {
    clearTimeout(this.inactivityInterval);
    this.router.navigate([
      `${this.frodoContext}/${this.paths.PROCESSING_ORDER}`,
      {
        type: this.type,
        flow: this.flow,
        scheduleId: this.scheduleId,
      },
    ]);
  }

  errorActions() {
    clearTimeout(this.inactivityInterval);
    this.isLoading = false;
    this.dialogInstance.close();
    this.router.navigate([this.loadFlowOrigin.consumeExit(`${this.frodoContext}`)]);
  }

  getDispensesCreated(scheduleId: string) {
    this.subscription.add(
      this.helmsdeepService.getScheduleById(this.scheduleId, { includeDispenses: true }).subscribe(
        (schedule: Order) => {
          const dispensesToUndo = schedule.dispenses
            .filter((dispense) => dispense.state === 'loading')
            .map((dispense) => ({
              resourceId: dispense.id,
              type: 'DO_UNDO',
              params: {},
            }));
          if (dispensesToUndo.length) this.doActionsOrder(dispensesToUndo, scheduleId);
        },
        (error) => {
          this.apiErrorHandlerService.handleApiError(error, '');
          this.errorActions();
        }
      )
    );
  }

  doActionsOrder(dispensesToUndo: IActions[], scheduleId: string) {
    this.subscription.add(
      this.helmsdeepService.actions(dispensesToUndo).subscribe(() => {
        this.subscription.add(
          this.helmsdeepService.deleteOrder(scheduleId).subscribe(() => {
            this.errorActions();
          })
        );
      })
    );
  }

  createDispense(verify: boolean): void {
    const createDispense = new CreateDispense(
      this.scheduleId,
      this.allowAlphanumeric,
      this.digitLength,
      PRESCRIPTIVE_FLOW,
      this.expireLoaded,
      verify
    );
    const newDispenseId = createDispense.newDispense.id;

    this.subscription.add(
      this.helmsdeepService.createDispenses([createDispense.newDispense]).subscribe(
        () => {
          this.startInactivityTimer();

          this.store
            .select(isSizeSelectionAvailable)
            .pipe(take(1))
            .subscribe((sizeSelectionAvailable) => {
              if (sizeSelectionAvailable && this.canNavigateToSizeSelector()) {
                this.orderProcessingService.orderToProcess = {
                  ...this.orderProcessingService.orderToProcess,
                  dispenseId: newDispenseId,
                };
                this.dialogInstance.close();
                this.goToSizeSelector();
                return;
              }

              this.addCompartment(newDispenseId);
            });
        },
        (error) => {
          this.dialogInstance.close();
          this.isLoading = false;
          this.apiErrorHandlerService.handleApiError(error, '');
        }
      )
    );
  }

  addCompartment(newDispenseId: string): void {
    const { side } = this.routeData;
    this.subscription.add(
      this.helmsdeepService.addComparment(newDispenseId, side).subscribe(
        () => {
          this.loadingStore.decreaseSpacesAvailable();
          this.dialogInstance.close();
          this.isLoading = false;
        },
        (error) => {
          this.apiErrorHandlerService.handleApiError(error, this.noSpaceAvailable);
          this.getDispensesCreated(this.scheduleId);
        }
      )
    );
  }

  refresh() {
    const now = new Date().getTime();
    const timeSince = now - this.latestTap;
    if (timeSince < 600 && timeSince > 0) {
      this.onCancel();
    }
    this.latestTap = new Date().getTime();
  }

  ngOnDestroy(): void {
    clearInterval(this.cancelOrderInterval);
    clearTimeout(this.inactivityInterval);
    this.subscription.unsubscribe();
  }

  private isPrescriptiveFlow(): boolean {
    return this.flow === PRESCRIPTIVE_FLOW;
  }

  private canNavigateToSizeSelector(): boolean {
    const arrayDevices: DeviceType[] = [DEVICE_TYPES.ARRAY_RL, DEVICE_TYPES.ARRAY_FL];
    return arrayDevices.includes(this.deviceType) && this.isPrescriptiveFlow();
  }

  private goToSizeSelector(): void {
    this.utils.redirect(`${this.frodoContext}/${this.paths.SIZE_SELECTOR}`);
  }
}
